import PostEditor from '@/components/PostEditor'
import RelayIcon from '@/components/RelayIcon'
import RelayInfo from '@/components/RelayInfo'
import RelaySetInfo from '@/components/RelaySetInfo'
import FollowSetInfo from '@/components/FollowSetInfo'
import { Button } from '@/components/ui/button'
import { simplifyUrl, normalizeUrl } from '@/lib/url'
import { loadRelayInfo } from '@nostr/gadgets/relays'
import PrimaryPageLayout from '@/layouts/PrimaryPageLayout'
import {
  unwrappedFeedAtom,
  includeRepliesAtom,
  switchFeed,
  trackRecentRelayFeed
} from '@/atoms/feed'
import { checkLogin, useCurrentPubkey } from '@/atoms/session'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { outboxStatusAtom } from '@/services/outbox.service'
import { TPageRef } from '@/types'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  InformationCircleIcon,
  PencilEdit01Icon,
  SwatchIcon,
  RefreshDotIcon,
  Cancel01Icon,
  Loading04Icon,
  Contact01Icon,
  Folder01Icon,
  Tag01Icon
} from '@hugeicons/core-free-icons'
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import FollowingFeed from './FollowingFeed'
import FollowSetFeed from './FollowSetFeed'
import HardcodedFeed from './HardcodedFeed'
import HardcodedFeedTitle from './HardcodedFeedTitle'
import NormalFeed from '@/components/NormalFeed'

const NoteListPage = forwardRef((_, ref) => {
  const { t } = useTranslation()
  const layoutRef = useRef<TPageRef>(null)
  const pubkey = useCurrentPubkey()
  const feed = useAtomValue(unwrappedFeedAtom)
  const [showDetails, setShowRelayDetails] = useState(false)
  useImperativeHandle(ref, () => layoutRef.current)

  const content: React.ReactNode = useMemo(() => {
    if (!feed) {
      return <div className="text-center text-sm text-muted-foreground">{t('loading...')}</div>
    } else if ('pubkey' in feed) {
      return <FollowingFeed />
    } else if ('followSet' in feed) {
      return (
        <>
          {showDetails && <FollowSetInfo followSet={feed.followSet} className="mb-2 pt-3" />}
          <FollowSetFeed followSet={feed.followSet} />
        </>
      )
    } else if ('relay' in feed) {
      return (
        <>
          {showDetails && <RelayInfo url={feed.relay} className="mb-2 pt-3" />}
          <NormalFeed
            subRequests={[
              {
                source: 'relays' as const,
                urls: [feed.relay],
                filter: {
                  kinds: window.hallway.universe.feedKinds
                }
              }
            ]}
            showRelayCloseReason
          />
        </>
      )
    } else if ('hardcodedFeed' in feed) {
      const hardcodedItem = window.hallway.universe.hardcodedFeeds[feed.hardcodedFeed]
      const singleRelayUrl =
        hardcodedItem?.relays?.length === 1 && !hardcodedItem?.pubkeys?.length
          ? hardcodedItem.relays[0]
          : undefined
      return (
        <>
          {showDetails && singleRelayUrl && (
            <RelayInfo url={singleRelayUrl} className="mb-2 pt-3" />
          )}
          <HardcodedFeed />
        </>
      )
    } else if ('relaySet' in feed) {
      return (
        <>
          {showDetails && <RelaySetInfo relaySet={feed.relaySet} className="mb-2 pt-3" />}
          <NormalFeed
            subRequests={
              feed.relaySet
                ? [
                    {
                      source: 'relays' as const,
                      urls: feed.relaySet.items,
                      filter: {
                        kinds: window.hallway.universe.feedKinds
                      }
                    }
                  ]
                : []
            }
            showRelayCloseReason
          />
        </>
      )
    }
  }, [feed, pubkey, showDetails])

  return (
    <PrimaryPageLayout
      pageName="home"
      ref={layoutRef}
      titlebar={
        <NoteListPageTitlebar
          layoutRef={layoutRef}
          showDetails={showDetails}
          setShowRelayDetails={
            feed &&
            ('relay' in feed ||
              'relaySet' in feed ||
              'followSet' in feed ||
              ('hardcodedFeed' in feed &&
                window.hallway.universe.hardcodedFeeds[feed.hardcodedFeed]?.relays?.length === 1 &&
                !window.hallway.universe.hardcodedFeeds[feed.hardcodedFeed]?.pubkeys?.length))
              ? setShowRelayDetails
              : undefined
          }
        />
      }
      displayScrollToTopButton
    >
      {content}
    </PrimaryPageLayout>
  )
})
NoteListPage.displayName = 'NoteListPage'
export default NoteListPage

function NoteListPageTitlebar({
  layoutRef,
  showDetails,
  setShowRelayDetails
}: {
  layoutRef?: React.RefObject<TPageRef>
  showDetails?: boolean
  setShowRelayDetails?: Dispatch<SetStateAction<boolean>>
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const feed = useAtomValue(unwrappedFeedAtom)
  const includeReplies = useAtomValue(includeRepliesAtom)
  const outbox = useAtomValue(outboxStatusAtom)
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [validating, setValidating] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const urlRef = useRef(feed && 'relay' in feed ? feed.relay : undefined)
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  return (
    <div className="flex gap-1 items-center h-full justify-between">
      {feed && ('pubkey' in feed || 'followSet' in feed) && (
        <div className="flex gap-2 items-center pl-3 flex-1 min-w-0">
          <HugeiconsIcon
            icon={'followSet' in feed ? Tag01Icon : Contact01Icon}
            className="shrink-0"
          />
          <div className="text-lg font-semibold truncate flex-1 min-w-0">
            {'followSet' in feed
              ? `${feed.followSet.title} (${feed.followSet.items.length ?? 0})`
              : includeReplies
                ? t('Following (with replies)')
                : t('Following (only root notes)')}
          </div>
          {outbox.syncing && (
            <div className="shrink-0 px-2 flex gap-2 items-center">
              <HugeiconsIcon icon={RefreshDotIcon} />
              <span>
                {t('Syncing {{current}}/{{total}}', {
                  current: outbox.current,
                  total: outbox.total
                })}
              </span>
            </div>
          )}
        </div>
      )}

      {feed && !('pubkey' in feed) && !('followSet' in feed) && (
        <div className="flex gap-2 items-center pl-3 flex-1 min-w-0">
          {'relay' in feed && (
            <>
              <RelayIcon url={feed.relay} iconSize={14} className="w-6 h-6 shrink-0" />
              {editing ? (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <input
                      ref={inputRef}
                      value={editValue}
                      onChange={(e) => {
                        setEditValue(e.target.value)
                        setEditError(null)
                      }}
                      onBlur={submitEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitEdit()
                        if (e.key === 'Escape') cancelEdit()
                      }}
                      className="bg-transparent border-b border-foreground text-lg font-semibold outline-none min-w-0 flex-1 disabled:opacity-50"
                      disabled={validating}
                    />
                    {validating && (
                      <HugeiconsIcon
                        icon={Loading04Icon}
                        className="w-4 h-4 animate-spin shrink-0"
                      />
                    )}
                    <button
                      onMouseDown={cancelEdit}
                      className="text-muted-foreground hover:text-foreground p-1 shrink-0"
                      tabIndex={-1}
                    >
                      <HugeiconsIcon icon={Cancel01Icon} className="w-4 h-4" />
                    </button>
                  </div>
                  {editError && <div className="text-xs text-destructive mt-0.5">{editError}</div>}
                </div>
              ) : (
                <div
                  className="text-lg font-semibold truncate cursor-text hover:bg-accent/50 rounded px-1 -mx-1"
                  onClick={startEditing}
                >
                  {simplifyUrl(feed.relay)}
                </div>
              )}
            </>
          )}
          {'relaySet' in feed && (
            <>
              <HugeiconsIcon icon={Folder01Icon} size={14} className="w-6 h-6 shrink-0" />
              <div className="text-lg font-semibold truncate">
                {feed.relaySet.title} ({feed.relaySet.items.length})
              </div>
            </>
          )}
          {'hardcodedFeed' in feed && (
            <HardcodedFeedTitle item={window.hallway.universe.hardcodedFeeds[feed.hardcodedFeed]} />
          )}
        </div>
      )}

      <div className="shrink-0 flex gap-1 items-center">
        {setShowRelayDetails && (
          <Button
            variant="ghost"
            size="titlebar-icon"
            onClick={(e) => {
              e.stopPropagation()
              setShowRelayDetails((show) => !show)

              if (!showDetails) {
                layoutRef?.current?.scrollToTop('smooth')
              }
            }}
            className={showDetails ? 'bg-accent/50' : ''}
          >
            <HugeiconsIcon icon={InformationCircleIcon} />
          </Button>
        )}

        {isSmallScreen && (
          <>
            <SearchButton />
            <PostButton />
          </>
        )}
      </div>
    </div>
  )

  function startEditing() {
    if (!feed || !('relay' in feed)) return
    setEditValue(simplifyUrl(feed.relay ?? ''))
    setEditError(null)
    setEditing(true)
  }

  async function submitEdit() {
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }

    const trimmed = editValue.trim()
    if (!trimmed) {
      setEditing(false)
      return
    }

    const normalized = normalizeUrl(trimmed)
    if (normalized === urlRef.current) {
      setEditing(false)
      return
    }

    setValidating(true)
    setEditError(null)

    const info = await loadRelayInfo(normalized)

    setValidating(false)

    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }

    if (!info) {
      setEditError(t('Relay did not respond'))
      inputRef.current?.focus()
      return
    }

    setEditValue(trimmed)
    setEditing(false)
    switchFeed({ relay: normalized })
    trackRecentRelayFeed(normalized)
  }

  function cancelEdit() {
    cancelledRef.current = true
    setEditing(false)
    setEditError(null)
  }
}

function PostButton() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        variant="ghost"
        size="titlebar-icon"
        onClick={(e) => {
          e.stopPropagation()
          checkLogin(() => {
            setOpen(true)
          })
        }}
      >
        <HugeiconsIcon icon={PencilEdit01Icon} />
      </Button>
      <PostEditor open={open} setOpen={setOpen} />
    </>
  )
}

function SearchButton() {
  return (
    <Button
      variant="ghost"
      size="titlebar-icon"
      onClick={() => document.dispatchEvent(new Event('quick-switch-toggle'))}
    >
      <HugeiconsIcon icon={SwatchIcon} />
    </Button>
  )
}
